import React, { createContext, useContext, useState, useEffect } from 'react'
import {
  auth,
  authHelpers,
  clientService,
  invoiceService,
  settingsService,
  logoService,
  notificationService
} from '../services/firebase'

const AppContext = createContext()

// Default settings for new users
const defaultSettings = {
  businessName: 'nCinq Solutions',
  businessEmail: 'hello@ncinq.com',
  businessPhone: '+1 (555) 000-0000',
  businessAddress: '123 Innovation Drive\nTech City, TC 12345',
  logo: null,
  currency: 'GYD',
  taxRate: 0,
  taxLabel: 'Tax',
  paymentTerms: 30,
  invoicePrefix: 'INV-',
  notifications: {
    invoiceSent: true,
    invoicePaid: true,
    invoiceOverdue: true,
    paymentReceived: true
  }
}

export function AppProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [settings, setSettings] = useState(defaultSettings)
  const [clients, setClients] = useState([])
  const [invoices, setInvoices] = useState([])
  const [notifications, setNotifications] = useState([])
  const [inbox, setInbox] = useState([])

  // Initialize auth listener
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const unsubscribe = authHelpers.onAuthChange(async (firebaseUser) => {
          if (firebaseUser) {
            setUser(firebaseUser)
            // Load user data
            await loadUserData(firebaseUser.uid)
          } else {
            // No user signed in - sign out and redirect to login
            setUser(null)
            setClients([])
            setInvoices([])
          }
          setLoading(false)
        })

        return () => unsubscribe()
      } catch (error) {
        console.error('Auth initialization error:', error)
        setLoading(false)
      }
    }

    initializeAuth()
  }, [])

  // Load user data from Firebase
  const loadUserData = async (userId) => {
    try {
      // Load settings
      const userSettings = await settingsService.getSettings(userId)
      if (userSettings) {
        setSettings(userSettings)
      } else {
        // First time user - create default settings
        await settingsService.createUserSettings(userId, defaultSettings)
        setSettings(defaultSettings)
      }

      // Load clients
      const userClients = await clientService.getClients(userId)
      setClients(userClients)

      // Load invoices
      const userInvoices = await invoiceService.getInvoices(userId)
      setInvoices(userInvoices)

    } catch (error) {
      console.error('Error loading user data:', error)
      throw error
    }
  }

  // Calculate dashboard metrics
  const dashboardMetrics = React.useMemo(() => {
    const now = new Date()
    const thisMonth = now.getMonth()
    const thisYear = now.getFullYear()

    const paidThisMonth = invoices
      .filter(inv => {
        if (inv.status !== 'paid' || !inv.paidAt) return false
        const paidDate = inv.paidAt.toDate ? inv.paidAt.toDate() : new Date(inv.paidAt)
        return paidDate.getMonth() === thisMonth && paidDate.getFullYear() === thisYear
      })
      .reduce((sum, inv) => sum + inv.total, 0)

    const outstanding = invoices
      .filter(inv => ['sent', 'viewed', 'partial', 'overdue'].includes(inv.status))
      .reduce((sum, inv) => sum + (inv.total - (inv.partialAmount || 0)), 0)

    const overdue = invoices
      .filter(inv => inv.status === 'overdue')
      .reduce((sum, inv) => sum + inv.total, 0)

    return {
      paidThisMonth,
      outstanding,
      overdue,
      draftCount: invoices.filter(inv => inv.status === 'draft').length,
      pendingCount: invoices.filter(inv => ['sent', 'viewed', 'partial'].includes(inv.status)).length,
      totalClients: clients.length,
      totalInvoices: invoices.length
    }
  }, [invoices, clients])

  // Client operations
  const addClient = async (clientData) => {
    const clientId = await clientService.addClient(user.uid, clientData)
    await loadUserData(user.uid)
    return clientId
  }

  const updateClient = async (id, clientData) => {
    await clientService.updateClient(user.uid, id, clientData)
    await loadUserData(user.uid)
  }

  const deleteClient = async (id) => {
    await clientService.deleteClient(user.uid, id)
    await loadUserData(user.uid)
  }

  // Invoice operations
  const addInvoice = async (invoiceData) => {
    const invoiceId = await invoiceService.addInvoice(user.uid, invoiceData)
    await loadUserData(user.uid)
    return invoiceId
  }

  const updateInvoice = async (id, invoiceData) => {
    await invoiceService.updateInvoice(user.uid, id, invoiceData)
    await loadUserData(user.uid)
  }

  const deleteInvoice = async (id) => {
    await invoiceService.deleteInvoice(user.uid, id)
    await loadUserData(user.uid)
  }

  const sendInvoice = async (id) => {
    await invoiceService.sendInvoice(user.uid, id)
    addNotification('Invoice sent successfully', 'success')
    await loadUserData(user.uid)
  }

  const markAsPaid = async (id, amount = null) => {
    await invoiceService.markAsPaid(user.uid, id, amount)
    addNotification('Invoice marked as paid', 'success')
    await loadUserData(user.uid)
  }

  const recordPayment = async (id, amount) => {
    await invoiceService.recordPayment(user.uid, id, { amount })
    addNotification('Payment recorded successfully', 'success')
    await loadUserData(user.uid)
  }

  // Notification system
  const addNotification = (message, type = 'info') => {
    const id = Date.now().toString()
    setNotifications(prev => [...prev, { id, message, type, timestamp: new Date().toISOString() }])
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id))
    }, 4000)
  }

  // Settings update
  const updateSettings = async (newSettings) => {
    await settingsService.updateSettings(user.uid, newSettings)
    setSettings(newSettings)
    addNotification('Settings updated successfully', 'success')
  }

  // Logo upload
  const uploadLogo = async (file) => {
    try {
      // Delete old logo if exists
      if (settings.logo) {
        await logoService.deleteLogo(user.uid, settings.logo)
      }

      // Upload new logo
      const logoUrl = await logoService.uploadLogo(user.uid, file)

      // Update settings with new logo URL
      const newSettings = { ...settings, logo: logoUrl }
      await settingsService.updateSettings(user.uid, newSettings)
      setSettings(newSettings)

      addNotification('Logo uploaded successfully', 'success')
      return logoUrl
    } catch (error) {
      console.error('Error uploading logo:', error)
      addNotification('Failed to upload logo', 'error')
      throw error
    }
  }

  // Delete logo
  const deleteLogo = async () => {
    try {
      if (settings.logo) {
        await logoService.deleteLogo(user.uid, settings.logo)

        const newSettings = { ...settings, logo: null }
        await settingsService.updateSettings(user.uid, newSettings)
        setSettings(newSettings)

        addNotification('Logo removed', 'success')
      }
    } catch (error) {
      console.error('Error deleting logo:', error)
      addNotification('Failed to remove logo', 'error')
    }
  }

  // Get invoice by ID
  const getInvoiceById = (id) => {
    return invoices.find(inv => inv.id === id)
  }

  // Get client by ID
  const getClientById = (id) => {
    return clients.find(c => c.id === id)
  }

  // Auth functions
  const signIn = async (email, password) => {
    setLoading(true)
    try {
      await authHelpers.signIn(email, password)
      addNotification('Welcome back!', 'success')
    } catch (error) {
      addNotification(error.message, 'error')
      throw error
    } finally {
      setLoading(false)
    }
  }

  const signUp = async (email, password) => {
    setLoading(true)
    try {
      await authHelpers.signUp(email, password)
      addNotification('Account created!', 'success')
    } catch (error) {
      addNotification(error.message, 'error')
      throw error
    } finally {
      setLoading(false)
    }
  }

  const signInWithGoogle = async () => {
    setLoading(true)
    try {
      await authHelpers.signInWithGoogle()
      addNotification('Welcome!', 'success')
    } catch (error) {
      addNotification(error.message, 'error')
      throw error
    } finally {
      setLoading(false)
    }
  }

  const signOut = async () => {
    try {
      await authHelpers.signOut()
      setUser(null)
      setSettings(defaultSettings)
      setClients([])
      setInvoices([])
      addNotification('Signed out successfully', 'success')
    } catch (error) {
      addNotification(error.message, 'error')
    }
  }

  const updateUserProfile = async (displayName) => {
    try {
      await authHelpers.updateUserProfile(user, { displayName })
      setUser({ ...user, displayName })
      addNotification('Profile updated successfully', 'success')
    } catch (error) {
      addNotification('Failed to update profile', 'error')
      throw error
    }
  }

  // Subscribe to persistent notifications
  useEffect(() => {
    let unsubscribe
    if (user?.uid) {
      unsubscribe = notificationService.subscribeToNotifications(user.uid, (data) => {
        setInbox(data)
      })
    } else {
      setInbox([])
    }
    return () => unsubscribe && unsubscribe()
  }, [user])

  const markAsRead = async (id) => {
    await notificationService.markAsRead(id)
  }

  const markAllAsRead = async () => {
    if (user?.uid) {
      await notificationService.markAllAsRead(user.uid)
    }
  }

  const value = {
    // State
    user,
    loading,
    settings,
    clients,
    invoices,
    notifications, // Ephemeral toasts
    inbox,        // Persistent notifications
    dashboardMetrics,

    // Functions
    addClient,
    updateClient,
    deleteClient,
    addInvoice,
    updateInvoice,
    deleteInvoice,
    sendInvoice,
    markAsPaid,
    recordPayment,
    getInvoiceById,
    getClientById,
    updateSettings,
    uploadLogo,
    deleteLogo,
    addNotification,
    markAsRead,
    markAllAsRead,

    // Auth
    signIn,
    signUp,
    signInWithGoogle,
    signOut,
    updateUserProfile
  }

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  )
}

export function useApp() {
  const context = useContext(AppContext)
  if (!context) {
    throw new Error('useApp must be used within an AppProvider')
  }
  return context
}
